<template>
  <div class="plan-select">
    <div class="step-badge">
      <i class="el-icon-s-order"></i>
      <span>{{ $t('buy.steps.badge.step1') }}</span>
    </div>
    <!-- 货币选择器（仅 MT） -->
    <div class="currency-selector" v-if="showCurrencySelector">
      <span class="currency-selector-label">{{ $t('buy.currency.label') }}</span>
      <el-radio-group v-model="localCurrency" size="small" @change="onCurrencyChange">
        <el-radio-button v-for="opt in currencyOptions" :key="opt.value" :label="opt.value">
          {{ opt.label }}
        </el-radio-button>
      </el-radio-group>
    </div>
    <!-- 计费周期切换 -->
    <div class="billing-toggle">
      <span :class="{ active: billingCycle === 'one_year' }">{{ $t('buy.summary.year1') }}</span>
      <el-switch 
        v-model="isTwoYears" 
        @change="toggleBilling"
        active-color="#6366f1"
        inactive-color="#94a3b8"
      />
      <span :class="{ active: billingCycle === 'two_year' }">
        {{ $t('buy.summary.year2') }}
        <el-tag size="mini" type="success" effect="dark" class="discount-tag">
          {{ $t('buy.plan.savePercent', { percent: 10 }) }}
        </el-tag>
      </span>
    </div>

    <!-- 方案卡片 -->
    <div class="plans-grid" v-if="visiblePlans.length">
      <div 
        v-for="plan in visiblePlans"
        :key="plan.id"
        class="plan-card"
        :class="{ 
          selected: selectedPlan === plan.id,
          popular: plan.popular 
        }"
        @click="selectPlan(plan.id)"
      >
        <!-- 推荐标签 -->
        <div class="popular-badge" v-if="plan.popular">
          <i class="el-icon-star-on"></i>
          {{ $t('buy.plan.mostPopular') }}
        </div>
        
        <!-- 方案头部 -->
        <div class="plan-header">
          <div class="plan-icon" :style="{ background: plan.gradient }">
            <i :class="plan.icon"></i>
          </div>
          <h3 class="plan-name">{{ plan.displayName || $t(`buy.plan.plans.${plan.id}.name`) }}</h3>
          <p class="plan-desc">{{ $t(`buy.plan.plans.${plan.id}.desc`) }}</p>
        </div>

        <!-- 价格 -->
        <div class="plan-price">
          <span class="currency">{{ currency }}</span>
          <span class="amount">{{ getPrice(plan) }}</span>
          <span class="period">/ {{ billingCycle === 'two_year' ? $t('buy.summary.year2') : $t('buy.summary.year1') }}</span>
        </div>
        <div class="price-note" v-if="billingCycle === 'two_year'">
          {{ $t('buy.plan.billedForTwoYears') }}
        </div>

        <!-- 功能列表 -->
        <ul class="plan-features">
          <li v-for="(feature, fIdx) in plan.features" :key="fIdx">
            <i class="el-icon-check" :style="{ color: plan.checkColor }"></i>
            {{ $t(`buy.plan.plans.${plan.id}.features.${fIdx}`) }}
          </li>
        </ul>

        <!-- 选择按钮 -->
        <el-button 
          :type="selectedPlan === plan.id ? 'primary' : 'default'"
          class="select-btn"
          :class="{ 'selected-btn': selectedPlan === plan.id }"
        >
          {{ selectedPlan === plan.id ? $t('buy.plan.selected') : $t('buy.plan.selectPlan') }}
          <i v-if="selectedPlan === plan.id" class="el-icon-check"></i>
        </el-button>
      </div>
    </div>

    <div class="plans-empty" v-else>
      <i class="el-icon-warning-outline"></i>
      <span>{{ $t('buy.plan.noPlans') }}</span>
    </div>

    <!-- 功能对比表 -->
    <div class="comparison-section" v-if="visiblePlans.length">
      <h3 class="comparison-title" @click="showComparison = !showComparison">
        <i :class="showComparison ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        {{ $t('buy.plan.compareFeatures') }}
      </h3>
      <transition name="el-zoom-in-top">
        <div v-show="showComparison" class="comparison-table-wrapper">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>{{ $t('buy.plan.feature') }}</th>
                <th v-for="plan in visiblePlans" :key="plan.id">
                  {{ $t(`buy.plan.plans.${plan.id}.name`) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rIdx) in comparisonRows" :key="rIdx">
                <td class="feature-name">{{ $t(`buy.plan.comparison.${row.key}`) }}</td>
                <td v-for="plan in visiblePlans" :key="plan.id" class="feature-value">
                  <template v-if="typeof row[plan.id] === 'boolean'">
                    <i v-if="row[plan.id]" class="el-icon-check check-yes"></i>
                    <i v-else class="el-icon-close check-no"></i>
                  </template>
                  <template v-else>
                    {{ formatComparisonValue(row[plan.id]) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlanSelect',
  props: {
    purchaseConfig: {
      type: Object,
      default: null
    },
    currency: {
      type: String,
      default: 'RMB'
    },
    selectedPlanData: {
      type: Object,
      default: null
    },
    selectedCurrency: {
      type: String,
      default: 'EUR'
    },
    currencyOptions: {
      type: Array,
      default: () => []
    },
    showCurrencySelector: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isTwoYears: false,
      billingCycle: 'one_year',
      selectedPlan: 'professional',
      showComparison: false,
      localCurrency: null,
      availablePlanIds: ['starter', 'professional', 'enterprise'],
      plans: [
        {
          id: 'starter',
          icon: 'el-icon-s-flag',
          gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
          checkColor: '#3b82f6',
          oneYearPrice: 1800,
          twoYearsPrice: 3600,
          displayName: null,
          popular: false,
          features: [0, 1, 2, 3, 4,6,7]
        },
        {
          id: 'professional',
          icon: 'el-icon-s-opportunity',
          gradient: 'linear-gradient(135deg, #6366f1, #818cf8)',
          checkColor: '#6366f1',
          oneYearPrice: 2640,
          twoYearsPrice: 5280,
          displayName: null,
          popular: false,
          features: [0, 1, 2, 3, 4, 5, 6,7]
        },
        {
          id: 'enterprise',
          icon: 'el-icon-s-cooperation',
          gradient: 'linear-gradient(135deg, #0f172a, #334155)',
          checkColor: '#10b981',
          oneYearPrice: 3000,
          twoYearsPrice: 5400,
          displayName: null,
          popular: true,
          features: [0, 1, 2, 3, 4, 5, 6, 7,8]
        }
      ],
      comparisonRows: [
        { key: 'auditorSeats', starter:false, professional: true, enterprise: true },
        { key: 'auditStandards', starter: true, professional: true, enterprise: true },
        { key: 'supplierPortal', starter: true, professional: true, enterprise: true },
        { key: 'customTemplates', starter: '1', professional: '2', enterprise: { i18n: 'buy.plan.comparisonValues.unlimited' } },
        { key: 'aiRiskAssessment', starter: false, professional: true, enterprise: true },
        { key: 'apiAccess', starter: false, professional: false, enterprise: true },
        { key: 'ssoIntegration', starter: false, professional: true, enterprise: true },
                { key: 'clientSupper', starter: true, professional: true, enterprise: true },
        { key: 'dataRetention', starter: { i18n: 'buy.plan.comparisonValues.sixMonths' }, professional: { i18n: 'buy.plan.comparisonValues.oneYear' }, enterprise: { i18n: 'buy.plan.comparisonValues.unlimited' } },
        { key: 'storage', starter: true, professional: true, enterprise:true },
          { key: 'ssl', starter: true, professional: true, enterprise:true },

      ]
    }
  },
  computed: {
    visiblePlans() {
      return this.plans.filter(plan => this.availablePlanIds.includes(plan.id))
    }
  },
  methods: {
    onCurrencyChange(value) {
      this.$emit('currency-change', value)
    },
    emitSelection() {
      if (!this.selectedPlan) return
      const plan = this.plans.find(p => p.id === this.selectedPlan)
      if (!plan) return
      this.$emit('plan-selected', {
        planId: this.selectedPlan,
        billingCycle: this.billingCycle,
        price: this.getPrice(plan)
      })
    },
    applySelectedPlanData() {
      if (!this.selectedPlanData) return
      if (this.selectedPlanData.planId) {
        this.selectedPlan = this.selectedPlanData.planId
      }
      if (this.selectedPlanData.billingCycle) {
        this.billingCycle = this.selectedPlanData.billingCycle
        this.isTwoYears = this.billingCycle === 'two_year'
      }
    },
    applyPurchaseConfig() {
      if (!this.purchaseConfig || !this.purchaseConfig.plans) return
      const configuredPlans = this.purchaseConfig.plans
      const ids = Object.keys(configuredPlans)
      this.plans = this.plans.map(plan => {
        const price = configuredPlans[plan.id]
        if (!price) return plan
        return {
          ...plan,
          displayName: price.name || plan.displayName,
          oneYearPrice: this.fromMinor(price.oneYear),
          twoYearsPrice: this.fromMinor(price.twoYears)
        }
      })
      this.availablePlanIds = ids
      if (ids.length && !ids.includes(this.selectedPlan)) {
        const preferredPlan = this.selectedPlanData && this.selectedPlanData.planId
        this.selectedPlan = ids.includes(preferredPlan) ? preferredPlan : ids[0]
      }
      if (!ids.length) {
        this.selectedPlan = null
      }
      this.$emit('validate', !!this.selectedPlan)
      this.emitSelection()
    },
    fromMinor(amount) {
      return Math.round(Number(amount || 0)) / 100
    },
    toggleBilling(val) {
      this.billingCycle = val ? 'two_year' : 'one_year'
      this.emitSelection()
    },
    selectPlan(id) {
      this.selectedPlan = id
      this.$emit('validate', true)
      this.emitSelection()
    },
    getPrice(plan) {
      return this.billingCycle === 'two_year' ? plan.twoYearsPrice : plan.oneYearPrice
    },
    formatComparisonValue(value) {
      if (value && typeof value === 'object' && value.i18n) {
        return this.$t(value.i18n)
      }
      return value
    }
  },
  created() {
    this.applySelectedPlanData()
    this.applyPurchaseConfig()
    this.$emit('validate', !!this.selectedPlan)
  },
  watch: {
    selectedCurrency: {
      handler(value) {
        if (value) {
          this.localCurrency = value
        }
      },
      immediate: true
    },
    purchaseConfig: {
      handler() {
        this.applyPurchaseConfig()
      },
      deep: true
    },
    selectedPlanData: {
      handler() {
        this.applySelectedPlanData()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.plan-select {
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 24px;
  i {
    font-size: 14px;
  }
}

.currency-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 24px;

  .currency-selector-label {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
  }

  ::v-deep .el-radio-button__inner {
    padding: 8px 20px;
    border-radius: 8px;
    font-weight: 600;
  }

  ::v-deep .el-radio-button:first-child .el-radio-button__inner {
    border-left: 1px solid #dcdfe6;
  }
}

.billing-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 40px;
  font-size: 15px;
  color: #94a3b8;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    transition: all 0.3s;
    &.active {
      color: #1e293b;
      font-weight: 600;
    }
  }

  .discount-tag {
    margin-left: 6px;
    border-radius: 20px;
    font-size: 12px;
    padding: 2px 8px;
    height: auto;
    line-height: 16px;
    vertical-align: middle;
  }

  ::v-deep .el-switch {
    flex: 0 0 auto;
    height: 24px;
    line-height: 24px;
  }

  ::v-deep .el-switch__core {
    width: 44px !important;
    height: 24px;
    border-radius: 999px;

    &::after {
      top: 1px;
      width: 20px;
      height: 20px;
    }
  }

  ::v-deep .el-switch.is-checked .el-switch__core::after {
    margin-left: -21px;
  }
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 50px;
}

.plans-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 56px 20px;
  margin-bottom: 50px;
  border: 1px dashed #e2e8f0;
  border-radius: 16px;
  color: #94a3b8;

  i {
    font-size: 32px;
  }

  span {
    font-size: 14px;
  }
}

.plan-card {
  position: relative;
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px 28px;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.08);
    border-color: #c7d2fe;
  }

  &.selected {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.15), 0 20px 40px rgba(99,102,241,0.12);
    background: linear-gradient(180deg, #faf5ff 0%, #fff 100%);
  }

  &.popular {
    transform: scale(1.03);
    &:hover {
      transform: scale(1.03) translateY(-6px);
    }
  }
}

.popular-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  padding: 5px 20px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(99,102,241,0.3);
  
  i {
    margin-right: 4px;
  }
}

.plan-header {
  text-align: center;
  margin-bottom: 24px;

  .plan-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    
    i {
      font-size: 26px;
      color: white;
    }
  }

  .plan-name {
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
  }

  .plan-desc {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }
}

.plan-price {
  text-align: center;
  margin-bottom: 6px;
  
  .currency {
    font-size: 22px;
    color: #1e293b;
    font-weight: 600;
    vertical-align: super;
  }
  
  .amount {
    font-size: 48px;
    font-weight: 800;
    color: #1e293b;
    letter-spacing: -2px;
    line-height: 1;
  }
  
  .period {
    font-size: 14px;
    color: #94a3b8;
    margin-left: 4px;
  }
}

.price-note {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 24px;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  flex: 1;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    font-size: 14px;
    color: #475569;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }

    i {
      font-size: 16px;
      font-weight: 700;
      flex-shrink: 0;
    }
  }
}

.select-btn {
  width: 100%;
  border-radius: 10px;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.3s;

  &.selected-btn {
    background: linear-gradient(135deg, #6366f1, #818cf8);
    border: none;
    color: white;
  }
}

.comparison-section {
  margin-top: 20px;

  .comparison-title {
    text-align: center;
    font-size: 16px;
    color: #6366f1;
    cursor: pointer;
    padding: 12px;
    
    &:hover {
      color: #4f46e5;
    }

    i {
      margin-right: 8px;
      transition: transform 0.3s;
    }
  }
}

.comparison-table-wrapper {
  overflow-x: auto;
  margin-top: 16px;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead {
    th {
      padding: 16px 12px;
      background: #f8fafc;
      font-weight: 600;
      color: #1e293b;
      text-align: center;
      border-bottom: 2px solid #e2e8f0;

      &:first-child {
        text-align: left;
      }
    }
  }

  tbody {
    tr {
      transition: background 0.2s;
      &:hover {
        background: #f8fafc;
      }
    }

    td {
      padding: 14px 12px;
      border-bottom: 1px solid #f1f5f9;
      text-align: center;
      color: #475569;

      &.feature-name {
        text-align: left;
        font-weight: 500;
        color: #334155;
      }
    }
  }

  .check-yes {
    color: #10b981;
    font-size: 18px;
    font-weight: 700;
  }

  .check-no {
    color: #cbd5e1;
    font-size: 16px;
  }
}

@media (max-width: 991px) {
  .plans-grid {
    grid-template-columns: 1fr;
    gap: 18px;
    margin-bottom: 32px;
  }

  .plan-card,
  .plan-card.popular {
    transform: none;

    &:hover {
      transform: none;
    }
  }

  .comparison-table {
    min-width: 620px;
  }
}

@media (max-width: 560px) {
  .plan-select {
    max-width: 100%;
  }

  .step-badge {
    padding: 5px 12px;
    border-radius: 999px;
    margin-bottom: 18px;
    font-size: 12px;
  }

  .billing-toggle {
    flex-wrap: wrap;
    gap: 10px 14px;
    margin-bottom: 28px;
    text-align: center;
  }

  .plan-card {
    border-width: 1px;
    border-radius: 12px;
    padding: 22px 16px 16px;
    box-shadow: none;

    &.selected {
      box-shadow: 0 0 0 2px rgba(99,102,241,0.1);
    }
  }

  .plan-header {
    text-align: left;
    margin-bottom: 18px;

    .plan-icon {
      width: 42px;
      height: 42px;
      margin: 0 0 12px;

      i {
        font-size: 20px;
      }
    }

    .plan-name {
      font-size: 18px;
    }
  }

  .plan-price {
    text-align: left;

    .currency {
      font-size: 16px;
    }

    .amount {
      font-size: 38px;
      letter-spacing: 0;
    }

    .period {
      display: block;
      margin-top: 6px;
      margin-left: 0;
    }
  }

  .price-note {
    text-align: left;
  }

  .plan-features {
    margin-bottom: 18px;

    li {
      padding: 6px 0;
      font-size: 13px;
    }
  }

  .comparison-section {
    margin-top: 12px;
    padding: 0;
    border-radius: 12px;
    background: #f8fafc;
  }

  .comparison-section .comparison-title {
    margin: 0;
    font-size: 14px;
  }
}
</style>
